import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Table,
  Typography,
} from '@mui/joy';
import type { DooHardwareFinish, StatusFilter } from '../types';
import {
  createHardwareFinish,
  deleteHardwareFinish,
  finishDependents,
  formatDependents,
  listHardwareFinishes,
  updateHardwareFinish,
} from '../data/repository';
import { catalogueStatus, matchesStatusFilter } from '../data/status';
import { useAsyncList } from './NamedEntitySection';
import {
  ConfirmDialog,
  DivestFields,
  StatusChip,
  StatusFilterSelect,
  Toolbar,
} from './FormControls';

export function HardwareFinishesSection() {
  const { rows, loading, error, reload } = useAsyncList(listHardwareFinishes);
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DooHardwareFinish | null>(null);
  const [name, setName] = useState('');
  const [hex, setHex] = useState('#d5d8dc');
  const [divest, setDivest] = useState(false);
  const [deprecatedDate, setDeprecatedDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteRow, setDeleteRow] = useState<DooHardwareFinish | null>(null);

  const visible = useMemo(
    () => rows.filter((row) => matchesStatusFilter(row, filter)),
    [rows, filter]
  );

  const openCreate = () => {
    setEditing(null);
    setName('');
    setHex('#d5d8dc');
    setDivest(false);
    setDeprecatedDate('');
    setFormError(null);
    setOpen(true);
  };

  const openEdit = (row: DooHardwareFinish) => {
    setEditing(row);
    setName(row.finish_name);
    setHex(row.swatch_hex || '#d5d8dc');
    setDivest(Boolean(row.divest));
    setDeprecatedDate(row.deprecated_date?.slice(0, 10) || '');
    setFormError(null);
    setOpen(true);
  };

  const payload = () => ({
    finish_name: name.trim(),
    swatch_hex: hex.trim() || null,
    divest,
    deprecated_date: deprecatedDate || null,
  });

  return (
    <Box>
      <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
        Metal and PVD finishes for hardware products (Chrome, Graphite, etc.). Separate from door slab paint colours.
      </Typography>
      {error ? <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert> : null}
      {formError && !open ? <Alert color="warning" sx={{ mb: 2 }}>{formError}</Alert> : null}
      <Toolbar>
        <StatusFilterSelect value={filter} onChange={setFilter} />
        <Button onClick={openCreate}>Add hardware finish</Button>
      </Toolbar>
      <Sheet variant="outlined" sx={{ overflow: 'auto' }}>
        <Table stickyHeader>
          <thead>
            <tr>
              <th>Finish name</th>
              <th>Swatch</th>
              <th>Status</th>
              <th>Deprecated date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5}>Loading…</td></tr>
            ) : (
              visible.map((row) => (
                <tr key={row.finish_id}>
                  <td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: row.swatch_hex || '#ccc',
                        }}
                      />
                      {row.finish_name}
                    </Box>
                  </td>
                  <td>{row.swatch_hex || '—'}</td>
                  <td><StatusChip status={catalogueStatus(row)} /></td>
                  <td>{row.deprecated_date?.slice(0, 10) || '—'}</td>
                  <td>
                    <Button size="sm" variant="plain" onClick={() => openEdit(row)}>Edit</Button>
                    <Button
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={async () => {
                        const dependents = await finishDependents(row.finish_id);
                        if (dependents.length) {
                          setFormError(
                            `Cannot delete “${row.finish_name}” while it is used by ${formatDependents(dependents)}. Divest or deprecate it instead.`
                          );
                          return;
                        }
                        setDeleteRow(row);
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ minWidth: 460 }}>
          <ModalClose />
          <Typography level="title-lg">{editing ? 'Edit hardware finish' : 'New hardware finish'}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {formError ? <Alert color="danger">{formError}</Alert> : null}
            <Input placeholder="Finish name" value={name} onChange={(e) => setName(e.target.value)} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Input type="color" value={hex} onChange={(e) => setHex(e.target.value)} sx={{ width: 72 }} />
              <Input placeholder="Swatch HEX" value={hex} onChange={(e) => setHex(e.target.value)} />
            </Box>
            <DivestFields
              divest={divest}
              deprecatedDate={deprecatedDate}
              onDivest={setDivest}
              onDeprecatedDate={setDeprecatedDate}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!name.trim()) {
                    setFormError('Finish name is required.');
                    return;
                  }
                  try {
                    if (editing) await updateHardwareFinish(editing.finish_id, payload());
                    else await createHardwareFinish(payload());
                    setOpen(false);
                    reload();
                  } catch (err) {
                    setFormError(err instanceof Error ? err.message : 'Save failed');
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteRow)}
        title="Delete hardware finish?"
        message={
          deleteRow
            ? `Delete “${deleteRow.finish_name}”? Prefer deprecation for established finishes.`
            : ''
        }
        onClose={() => setDeleteRow(null)}
        onConfirm={async () => {
          if (!deleteRow) return;
          try {
            await deleteHardwareFinish(deleteRow.finish_id);
            setDeleteRow(null);
            reload();
          } catch (err) {
            setDeleteRow(null);
            setFormError(err instanceof Error ? err.message : 'Delete failed');
          }
        }}
      />
    </Box>
  );
}
